import { useEffect, useRef, useState } from "react";
import {
  addPlaylistItem,
  composeSlide,
  createContent,
  listPlaylists,
  listSources,
  reviewSlide,
  updateContent,
  type ComposeAttachment,
  type ComposeMessage,
  type SlideFinding,
} from "../lib/resources.ts";
import type { Playlist } from "../lib/types.ts";
import InlineSvg from "../lib/InlineSvg.tsx";
import {
  ACCEPTED_TYPES,
  MAX_ATTACHMENTS,
  MAX_TOTAL_BYTES,
  attachmentBytes,
  attachmentFromUrl,
  readAttachment,
} from "../lib/attachments.ts";
import type { Orientation } from "../lib/types.ts";

/** Where a composer session starts from, when it isn't a blank page. */
export type ComposerSeed =
  /** Reopen a saved AI slide to change something about it. */
  | {
      kind: "revise";
      contentId: string;
      name: string;
      svg: string;
      orientation?: Orientation;
    }
  /** Start from an image already in the library — usually a page of the weekly
   *  PowerPoint that would look better redesigned than shown as-is. */
  | { kind: "image"; name: string; url: string };

/** Opening turns for a revision. The API talks to a model that requires the
 *  first turn to be the user's, so the saved SVG is introduced rather than
 *  dropped in as a bare assistant message. */
function seedMessages(svg: string): ComposeMessage[] {
  return [
    {
      role: "user",
      content:
        "This is the slide as it stands. Keep this design unless I ask for something different.",
    },
    { role: "assistant", content: svg },
  ];
}

// Chat-style AI slide composer. Describe a slide, attach a flyer, or reopen one
// that already exists — Claude returns an SVG sized to the chosen orientation.
export default function AIComposer({
  onClose,
  onSaved,
  seed,
}: {
  onClose: () => void;
  onSaved: () => void;
  seed?: ComposerSeed;
}) {
  const revising = seed?.kind === "revise" ? seed : null;

  const [orientation, setOrientation] = useState<Orientation>(
    revising?.orientation ?? "landscape",
  );
  const [messages, setMessages] = useState<ComposeMessage[]>(
    revising ? seedMessages(revising.svg) : [],
  );
  const [input, setInput] = useState("");
  const [pending, setPending] = useState<ComposeAttachment[]>([]);
  const [svg, setSvg] = useState<string | null>(revising?.svg ?? null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(revising ? `${revising.name} (updated)` : "");
  const [qrOn, setQrOn] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [findings, setFindings] = useState<SlideFinding[] | null>(null);
  const [checking, setChecking] = useState(false);
  // Where the finished slide goes. Saving used to put it in the content library
  // and stop there, so a volunteer composed a slide, saw it appear in Content,
  // and it played nowhere — the step that actually puts it on the wall was a
  // different screen. Empty string means "library only", which stays available
  // because it is the old behaviour and is what you want when building ahead.
  const [playlists, setPlaylists] = useState<Playlist[] | null>(null);
  const [target, setTarget] = useState<string>("");
  const fileInput = useRef<HTMLInputElement>(null);

  // Seeding from an image is a fetch, so it can't happen during render.
  const seedUrl = seed?.kind === "image" ? seed.url : null;
  const seedName = seed?.kind === "image" ? seed.name : null;
  useEffect(() => {
    if (!seedUrl || !seedName) return;
    let cancelled = false;
    setReading(true);
    attachmentFromUrl(seedUrl, seedName)
      .then((a) => {
        if (!cancelled) setPending([a]);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Couldn't load that image");
        }
      })
      .finally(() => {
        if (!cancelled) setReading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [seedUrl, seedName]);

  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setError(null);
    setReading(true);
    try {
      const next = [...pending];
      for (const file of Array.from(files)) {
        if (next.length >= MAX_ATTACHMENTS) {
          throw new Error(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        }
        next.push(await readAttachment(file));
      }
      // Every attachment is re-sent on each revision, so cap the whole set.
      const total = next.reduce((sum, a) => sum + attachmentBytes(a), 0);
      if (total > MAX_TOTAL_BYTES) {
        throw new Error(
          `Those files add up to ${(total / 1e6).toFixed(1)} MB; the limit is ${(
            MAX_TOTAL_BYTES / 1e6
          ).toFixed(1)} MB. Try attaching fewer.`,
        );
      }
      setPending(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read that file");
    } finally {
      setReading(false);
      // Let the same file be picked again after a removal.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    // The link is validated server-side (that is where it gets encoded); catch
    // only the blank case here, which is a slip rather than a bad address.
    const link = qrOn ? qrUrl.trim() : "";
    if (qrOn && !link) {
      setError("Add the link the QR code should open, or turn the QR code off.");
      return;
    }
    const turn: ComposeMessage = { role: "user", content: text };
    if (pending.length) turn.attachments = pending;
    const next = [...messages, turn];
    setMessages(next);
    setInput("");
    setPending([]);
    setBusy(true);
    setError(null);
    // The old findings describe the old slide.
    setFindings(null);
    try {
      const { svg: out } = await composeSlide(next, orientation, link || null);
      setMessages([...next, { role: "assistant", content: out }]);
      setSvg(out);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Generation failed");
      // drop the unanswered user turn so a retry doesn't duplicate it, and give
      // the volunteer their attachments back rather than making them re-pick
      setMessages(messages);
      setPending(turn.attachments ?? []);
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    if (!svg || checking) return;
    setChecking(true);
    setError(null);
    try {
      const { findings: found } = await reviewSlide(svg, orientation);
      setFindings(found);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Check failed");
    } finally {
      setChecking(false);
    }
  }

  /** `asCopy` matters when revising: overwriting the original is usually what
   *  you want, but not always, and the two are hard to undo apart afterwards. */
  // The playlist the PowerPoint source owns is the wall's — the same one the
  // church-app page and the EasyWorship deck read — so it is the default. Only
  // for a new slide: revising one already has a home, and moving it is the
  // playlist editor's job rather than a side effect of an edit.
  useEffect(() => {
    if (revising) return;
    let live = true;
    (async () => {
      try {
        const [lists, sources] = await Promise.all([listPlaylists(), listSources()]);
        if (!live) return;
        setPlaylists(lists);
        const wall = sources.find((s) => s.kind === "powerpoint" && s.playlistId);
        const fallback = lists.length === 1 ? lists[0].id : "";
        setTarget(wall?.playlistId ?? fallback);
      } catch {
        // Not being able to offer a destination must not stop a slide being
        // saved: the picker just does not appear and the old behaviour stands.
        if (live) setPlaylists([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [revising]);

  async function save(asCopy = false) {
    if (!svg) return;
    const slideName = name.trim() || revising?.name || "AI slide";
    setBusy(true);
    setError(null);
    try {
      if (revising && !asCopy) {
        await updateContent(revising.contentId, {
          name: slideName,
          data: { svg },
        });
      } else {
        const created = await createContent({
          name: slideName,
          type: "svg",
          data: { svg },
        });
        if (target) {
          // Appends; it does not rewrite the running order. A failure here is
          // reported without losing the slide — it is already in the library,
          // so the recovery is to add it from the playlist editor rather than
          // to compose it again.
          try {
            await addPlaylistItem(target, created.id);
          } catch (e) {
            setError(
              `Slide saved to the library, but adding it to the playlist failed: ${
                e instanceof Error ? e.message : "unknown error"
              }`,
            );
            setBusy(false);
            return;
          }
        }
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
      setBusy(false);
    }
  }

  // Only user turns are human-readable; assistant turns are raw SVG. The seeded
  // "this is the slide as it stands" turn is scaffolding, not something the
  // volunteer typed, so it is not shown back to them.
  const transcript = messages.filter(
    (m, i) => m.role === "user" && !(revising && i === 0),
  );

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="composer-head">
          <h2>{revising ? `Revise “${revising.name}”` : "Compose with AI"}</h2>
          <div className="seg">
            <button
              className={"seg-btn" + (orientation === "landscape" ? " seg-btn--on" : "")}
              onClick={() => setOrientation("landscape")}
            >
              Landscape
            </button>
            <button
              className={"seg-btn" + (orientation === "portrait" ? " seg-btn--on" : "")}
              onClick={() => setOrientation("portrait")}
            >
              Portrait
            </button>
          </div>
        </div>

        <div className="composer-body">
          <div className="composer-chat">
            {transcript.length === 0 && !svg && (
              <p className="muted">
                Describe the slide — include the real details (event, day, date,
                time, place). e.g. “Youth night, Wednesday Jan 14 at 6:30pm in
                the Fellowship Hall.” Or attach a flyer and let Claude read the
                details off it.
              </p>
            )}
            {transcript.length === 0 && svg && revising && (
              <p className="muted">
                Say what to change — e.g. “change the date to the 24th”, “make
                the time bigger”, “use the cream background”.
              </p>
            )}
            {transcript.length === 0 && !svg && seed?.kind === "image" && (
              <p className="muted">
                Starting from “{seed.name}”. Say what you want — e.g. “redesign
                this to be readable from across the room”.
              </p>
            )}
            {transcript.map((m, i) => (
              <div className="composer-msg" key={i}>
                {m.content}
                {m.attachments?.length ? (
                  <div className="composer-msg-files">
                    {m.attachments.map((a, j) => (
                      <span key={j}>📎 {a.name ?? "flyer"}</span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {busy && <div className="muted">Generating…</div>}
            {error && <div className="banner banner--error">{error}</div>}

            {findings && (
              <div className="findings">
                {findings.length === 0 ? (
                  <div className="banner banner--ok">
                    Checked — nothing to fix.
                  </div>
                ) : (
                  <>
                    <div className="findings-head">
                      Found {findings.length}{" "}
                      {findings.length === 1 ? "thing" : "things"} to look at:
                    </div>
                    <ul className="findings-list">
                      {findings.map((f, i) => (
                        <li key={i} className={`finding finding--${f.severity}`}>
                          <span className="finding-sev">{f.severity}</span>
                          <span>
                            {f.what}
                            {f.fix && <em> {f.fix}</em>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>

          <div className={"composer-preview composer-preview--" + orientation}>
            {svg ? (
              <InlineSvg className="composer-svg" svg={svg} />
            ) : (
              <span className="muted">Preview appears here</span>
            )}
          </div>
        </div>

        {pending.length > 0 && (
          <div className="composer-files">
            {pending.map((a, i) => (
              <span className="composer-file" key={i}>
                <span className="composer-file-name" title={a.name}>
                  📎 {a.name ?? "flyer"}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${a.name ?? "attachment"}`}
                  onClick={() => setPending(pending.filter((_, j) => j !== i))}
                  disabled={busy}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="composer-qr">
          <label className="composer-qr-toggle">
            <input
              type="checkbox"
              checked={qrOn}
              disabled={busy}
              onChange={(e) => {
                setQrOn(e.target.checked);
                setError(null);
              }}
            />
            QR code
          </label>
          {qrOn && (
            <input
              className="inline-input composer-qr-url"
              type="url"
              inputMode="url"
              placeholder="https://libertybiblechurch.org/give"
              aria-label="Link the QR code should open"
              value={qrUrl}
              disabled={busy}
              onChange={(e) => setQrUrl(e.target.value)}
            />
          )}
          {qrOn && (
            <span className="composer-qr-hint muted">
              Scanning opens this link. Check it before saving.
            </span>
          )}
        </div>

        <div className="composer-input">
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            hidden
            onChange={(e) => void addFiles(e.target.files)}
          />
          <button
            className="btn btn--ghost"
            type="button"
            title="Attach an event flyer (image or PDF)"
            onClick={() => fileInput.current?.click()}
            disabled={busy || reading || pending.length >= MAX_ATTACHMENTS}
          >
            {reading ? "Reading…" : "📎 Flyer"}
          </button>
          <textarea
            rows={2}
            placeholder={
              svg
                ? "Ask for changes…"
                : pending.length
                  ? "What should this slide say? e.g. “Make a slide from this flyer.”"
                  : "Describe the slide…"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button className="btn" onClick={send} disabled={busy || !input.trim()}>
            {svg ? "Revise" : "Generate"}
          </button>
        </div>

        <div className="modal-actions">
          {svg && (
            <>
              <button
                className="btn btn--ghost"
                onClick={check}
                disabled={busy || checking}
                title="Have Claude proofread this slide before you save it"
              >
                {checking ? "Checking…" : "🔍 Check slide"}
              </button>
              <input
                className="inline-input"
                placeholder="Slide name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {!revising && playlists && playlists.length > 0 && (
                <select
                  className="inline-input"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  title="Where this slide should play. The library keeps it without putting it on a screen."
                >
                  <option value="">Library only — not on a screen</option>
                  {playlists.map((pl) => (
                    <option key={pl.id} value={pl.id}>
                      Add to {pl.name}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {revising && (
            <button
              className="btn btn--ghost"
              onClick={() => save(true)}
              disabled={busy || !svg}
              title="Keep the original and save this as a separate slide"
            >
              Save as copy
            </button>
          )}
          <button className="btn" onClick={() => save(false)} disabled={busy || !svg}>
            {revising
              ? "Update slide"
              : target
                ? "Save and add to playlist"
                : "Add to library"}
          </button>
        </div>
      </div>
    </div>
  );
}
